import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { useCurrentStateAndParams } from "@uirouter/react";

import Sentry from "@similarweb/sentry-logger";

import useLobSites from "app/components/compare/WebsiteQueryBar/useLobSites";
import DurationService, { BasicDurations } from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";

interface LOBQueryParams {
    country: string;
    webSource: string;
    keys: string;
    from: string;
    to: string;
    includeSubDomains: boolean;
    isWindow: boolean;
    latest?: string;
}
const fetchService = DefaultFetchService.getInstance();

async function fetchLob<T>(endpoint: string, params: LOBQueryParams): Promise<T | null> {
    try {
        return await fetchService.get<T>(endpoint, params);
    } catch (err) {
        Sentry.error(`LOB fetch failed for ${endpoint}`, err);
        return null;
    }
}

export function useLobApi<T>(
    endpoint: string,
    queryKeyPrefix: string,
): {
    data: T | null;
    isLoading: boolean;
    isError: boolean;
} {
    const {
        params: { duration, key, comparedDuration, country, webSource },
    } = useCurrentStateAndParams();
    const { lobSites } = useLobSites();

    const { includedSites } = useMemo(() => {
        if (!key || !lobSites.length) {
            return { includedSites: [], excludedSites: [] };
        }

        const allSites = key.split(",").filter(Boolean);
        if (!allSites.length) {
            return { includedSites: [], excludedSites: [] };
        }

        const lobNamesSet = new Set(lobSites.map((site) => site.name));
        const included: string[] = [];
        const excluded: string[] = [];

        for (const site of allSites) {
            lobNamesSet.has(site) ? included.push(site) : excluded.push(site);
        }

        return { includedSites: included, excludedSites: excluded };
    }, [key, lobSites]);

    const { from, to, isWindow } = DurationService.getDurationData(duration, comparedDuration).forAPI;

    const apiParams: LOBQueryParams = useMemo(() => {
        const base: LOBQueryParams = {
            country,
            webSource,
            keys: includedSites.join(","),
            from,
            to,
            includeSubDomains: true,
            isWindow,
        };
        if (isWindow) {
            base.latest = BasicDurations.LAST_TWENTY_EIGHT_DAYS;
        }
        return base;
    }, [country, webSource, includedSites, from, to, isWindow]);

    const queryKey = useMemo(() => [queryKeyPrefix, apiParams], [queryKeyPrefix, apiParams]);

    const { data, isFetching, isError } = useQuery<T>({
        queryKey,
        queryFn: () => fetchLob<T>(endpoint, apiParams),
        enabled: !!includedSites.length,
    });

    return { data, isLoading: isFetching, isError };
}
